import { ISequenceStep } from '../../../interfaces/models/campaign';

function outputLanguageInstruction(
  aiLanguage: string | undefined,
  stepType: 'message' | 'post_comment' | 'email',
): string {
  const lang = (aiLanguage || 'Auto').trim();
  const noun = stepType === 'post_comment' ? 'comment' : stepType === 'email' ? 'email' : 'message';

  if (lang.toLowerCase() === 'auto') {
    return stepType === 'post_comment'
      ? 'Match the language of the post being commented on.'
      : `Write the ${noun} in English, even if the campaign goal is written in another language.`;
  }
  return `Write the ${noun} in ${lang}.`;
}

/**
 * English step instruction for AI message steps.
 * Campaign description is NOT embedded here — it is passed at generation time
 * as campaign context so the prompt UI stays language-neutral.
 */
export function buildMessageAiPrompt(
  messageIndex: number,
  totalMessages: number,
  aiLanguage?: string,
): string {
  const langLine = outputLanguageInstruction(aiLanguage, 'message');

  if (messageIndex > 0) {
    return `Write a brief personalized LinkedIn follow-up message for {{firstName}} at {{company}}.

This is follow-up ${messageIndex + 1} of ${totalMessages}. Keep it short (1–3 sentences), add new value, and do not repeat the first message verbatim. Tone: warm and professional.

The overall campaign goal will be provided separately — use it to shape the angle.

${langLine}`;
  }

  return `Write a personalized LinkedIn outreach message for {{firstName}} at {{company}} ({{title}}).

Tone: warm, professional, not pushy or salesy. 2–4 sentences. Reference their role or company when relevant.

The overall campaign goal will be provided separately — use it to shape the angle.

${langLine}`;
}

/** English step instruction for AI comment steps. */
export function buildCommentAiPrompt(aiLanguage?: string): string {
  const langLine = outputLanguageInstruction(aiLanguage, 'post_comment');

  return `Write a short, genuine comment (1–2 sentences) on the prospect's latest LinkedIn post.

React naturally to the post content — avoid generic praise. Use {{firstName}} only if it fits naturally.

The overall campaign goal will be provided separately — use it to shape the angle.

${langLine}`;
}

/**
 * Subject line for an AI email step.
 *
 * The instruction leans hard on "plain" because subject lines that read like
 * marketing get filtered before anyone sees them — the ones that read like a
 * colleague get opened.
 */
export function buildEmailSubjectAiPrompt(aiLanguage?: string): string {
  const langLine = outputLanguageInstruction(aiLanguage, 'email');

  return `Write a short subject line for a cold outreach email to {{firstName}} at {{company}}.

Under 50 characters. Plain and specific, like something a colleague would write — no exclamation marks, no capitalised words, no marketing phrases.

The overall campaign goal will be provided separately — use it to shape the angle.

${langLine}`;
}

/** Body for an AI email step. `emailIndex` distinguishes the first email from follow-ups. */
export function buildEmailBodyAiPrompt(
  emailIndex: number,
  totalEmails: number,
  aiLanguage?: string,
): string {
  const langLine = outputLanguageInstruction(aiLanguage, 'email');

  if (emailIndex > 0) {
    return `Write a brief follow-up email to {{firstName}} at {{company}}.

This is follow-up ${emailIndex + 1} of ${totalEmails}. Two or three sentences, add a new angle, and do not repeat the first email. End with one easy question.

The overall campaign goal will be provided separately — use it to shape the angle.

${langLine}`;
  }

  return `Write a cold outreach email to {{firstName}} at {{company}} ({{title}}).

Two or three short paragraphs. Open with a specific reason for writing to this person, state the point plainly, and end with one low-friction question. No sign-off — the signature is added automatically.

The overall campaign goal will be provided separately — use it to shape the angle.

${langLine}`;
}

/**
 * Fills empty AI prompt fields when a campaign description exists.
 * Description text is applied at send/preview time via campaign context — not copied into the prompt.
 */
export function applyDescriptionToSequence(
  description: string,
  steps: ISequenceStep[],
): ISequenceStep[] {
  if (!description.trim()) return steps;

  const messageStepIndices = steps
    .map((s, i) => (
      (s.type === 'message' || s.type === 'sn_message') && (s.messageMode ?? 'ai') === 'ai' ? i : -1
    ))
    .filter(i => i >= 0);
  const totalMessages = messageStepIndices.length;

  const emailStepIndices = steps
    .map((s, i) => (s.type === 'email' ? i : -1))
    .filter(i => i >= 0);
  const totalEmails = emailStepIndices.length;

  return steps.map((step, idx) => {
    if (
      (step.type === 'message' || step.type === 'sn_message')
      && (step.messageMode ?? 'ai') === 'ai'
      && !(step.messageAiPrompt?.trim())
    ) {
      const msgIdx = messageStepIndices.indexOf(idx);
      return {
        ...step,
        messageAiPrompt: buildMessageAiPrompt(Math.max(0, msgIdx), totalMessages, step.aiLanguage),
      };
    }
    if (
      step.type === 'post_comment'
      && (step.commentMode ?? 'ai') === 'ai'
      && !(step.commentAiPrompt?.trim())
    ) {
      return { ...step, commentAiPrompt: buildCommentAiPrompt(step.aiLanguage) };
    }
    if (step.type === 'email') {
      const emailIdx = Math.max(0, emailStepIndices.indexOf(idx));
      // A follow-up that replies in-thread inherits the original subject, so only
      // the first email in the sequence needs a subject prompt.
      const needsSubject = emailIdx === 0 || step.replyInThread === false;

      const next = { ...step };
      let changed = false;

      if (
        needsSubject
        && (step.emailSubjectMode ?? 'ai') === 'ai'
        && !(step.emailSubjectAiPrompt?.trim())
      ) {
        next.emailSubjectAiPrompt = buildEmailSubjectAiPrompt(step.aiLanguage);
        changed = true;
      }
      if (
        (step.emailBodyMode ?? 'ai') === 'ai'
        && !(step.emailBodyAiPrompt?.trim())
      ) {
        next.emailBodyAiPrompt = buildEmailBodyAiPrompt(emailIdx, totalEmails, step.aiLanguage);
        changed = true;
      }
      return changed ? next : step;
    }
    return step;
  });
}
